import * as React from 'react';

import { DescriptionPage_CourseType as CourseType } from '__generated__/graphql-types';
import { branch, compose } from 'recompose';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { useShowMonthlyToAnnualUpgradeOnXDP } from 'bundles/coursera-plus/utils/plusMonthlyToAnnualUpgradeUtils';
import type { EligibilityCheckProps } from 'bundles/coursera-plus/utils/withCourseraPlusProductEligibilityCheck';
import withCourseraPlusProductEligibilityCheck from 'bundles/coursera-plus/utils/withCourseraPlusProductEligibilityCheck';
import type { PropsFromCourseraPlusProductOwnerships } from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import withCourseraPlusProductOwnerships from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import type { PropsFromWithEnrollment } from 'bundles/enroll/components/xdp/withEnrollment';
import withEnrollment from 'bundles/enroll/components/xdp/withEnrollment';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import CourseraPlusOwnershipContext from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';

type PropsToInnerProvider = EligibilityCheckProps &
  PropsFromWithEnrollment &
  PropsFromCourseraPlusProductOwnerships & {
    children: React.ReactNode;
  };

type PropsToDataFetcher = {
  s12nId?: string;
  courseId?: string;
  courseType?: CourseType;
  isSpecialization: boolean;
};

const CourseraPlusOwnershipDataProviderInner = ({
  children,
  isCourseraPlusEligibleProduct,
  enrollmentAvailableChoices,
  ownsCourseraPlus,
  ownsCourseraLite,
}: PropsToInnerProvider) => {
  const isSubscribedToCourseraLite = ownsCourseraLite || enrollmentAvailableChoices?.isCourseraLiteSubscribed;
  const isSubscribedToCourseraPlus =
    ownsCourseraPlus ||
    enrollmentAvailableChoices?.isCourseraPlusMonthlySubscribed ||
    enrollmentAvailableChoices?.isCourseraPlusAnnualSubscribed;
  const isSubscribedToCPlusOrLite =
    enrollmentAvailableChoices?.canEnrollThroughCourseraPlus ||
    isSubscribedToCourseraLite ||
    isSubscribedToCourseraPlus;
  const isUserEligibleToSeeCta =
    enrollmentAvailableChoices?.canSubscribeOrEnrollInCourseraPlusEligibleProduct || isCourseraPlusEligibleProduct;
  const { showPlusMonthlyToAnnualUpgradeOnXDP, isEligibleForPlusMonthlyToAnnualUpgrade } =
    useShowMonthlyToAnnualUpgradeOnXDP();

  return (
    <CourseraPlusOwnershipContext.Provider
      value={{
        isSubscribedToCPlusOrLite,
        isUserEligibleToSeeCta,
        isCourseraPlusEligibleProduct: isUserEligibleToSeeCta,
        isSubscribedToCourseraLite,
        showPlusMonthlyToAnnualUpgradeButton: Boolean(isUserEligibleToSeeCta && showPlusMonthlyToAnnualUpgradeOnXDP),
        isEligibleForPlusMonthlyToAnnualUpgrade: Boolean(
          isUserEligibleToSeeCta && isEligibleForPlusMonthlyToAnnualUpgrade
        ),
      }}
    >
      {children}
    </CourseraPlusOwnershipContext.Provider>
  );
};

const CourseraPlusOwnershipDataFetcher = compose<PropsToInnerProvider, React.PropsWithChildren<PropsToDataFetcher>>(
  withEnrollment(true),
  // If EAC indicates the learner can subscribe or enroll into a C+ eligible product, then we don't need to fetch C+ product eligibility
  branch<PropsFromWithEnrollment>(
    ({ enrollmentAvailableChoices }) => !enrollmentAvailableChoices?.canSubscribeOrEnrollInCourseraPlusEligibleProduct,
    withCourseraPlusProductEligibilityCheck<PropsToDataFetcher>(true)
  ),
  // We only don't know the learner's C+ ownership status if either
  // 1) the product is C+ eligible and they are already enrolled (we don't know how they enrolled into it)
  // 2) the product is a Project/Guided Project and they can subscribe to Plus (we don't know if they own Lite)
  // If the learner is logged out, the HOC already skips the call
  // If the product is not C+ eligible, we don't need to know about C+ ownership status
  // If EAC says the learner can enroll through Plus, then they own Lite/Plus
  // If EAC doesn't say the learner can enroll through Plus, they they don't own Lite/Plus
  branch<PropsFromWithEnrollment & PropsToDataFetcher & EligibilityCheckProps>(
    ({ isCourseraPlusEligibleProduct, enrollmentAvailableChoices, courseType }) =>
      Boolean(
        (isCourseraPlusEligibleProduct && enrollmentAvailableChoices?.isEnrolled) ||
          (enrollmentAvailableChoices?.canSubscribeToCourseraPlus && courseType !== CourseType.StandardCourse)
      ),
    compose<PropsToInnerProvider, React.PropsWithChildren<PropsToDataFetcher & EligibilityCheckProps>>(
      withCourseraPlusProductOwnerships(true)
    )
  )
)(CourseraPlusOwnershipDataProviderInner);

export const CourseCourseraPlusOwnershipDataProvider = ({ children }: { children: React.ReactNode }) => {
  const pageData = useCoursePageData();

  if (pageData?.id) {
    return (
      <ErrorBoundaryWithTagging
        componentName="CourseCourseraPlusOwnershipDataProvider"
        errorComponent={<>{children}</>}
      >
        <CourseraPlusOwnershipDataFetcher
          courseId={pageData.id}
          courseType={pageData.courseType ?? undefined}
          isSpecialization={false}
        >
          {children}
        </CourseraPlusOwnershipDataFetcher>
      </ErrorBoundaryWithTagging>
    );
  } else {
    return <>{children}</>;
  }
};

export const SpecializationCourseraPlusOwnershipDataProvider = ({ children }: { children: React.ReactNode }) => {
  const pageData = useSpecializationPageData();

  if (pageData?.id) {
    return (
      <ErrorBoundaryWithTagging
        componentName="SpecializationCourseraPlusOwnershipDataProvider"
        errorComponent={<>{children}</>}
      >
        <CourseraPlusOwnershipDataFetcher s12nId={pageData.id} isSpecialization>
          {children}
        </CourseraPlusOwnershipDataFetcher>
      </ErrorBoundaryWithTagging>
    );
  } else {
    return <>{children}</>;
  }
};
